import { Component, Inject, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';

// translation service
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

// okta auth
import { OKTA_CONFIG } from '@okta/okta-angular';
import type { OktaAuth } from '@okta/okta-auth-js';

import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';

// services, models, and other imports can be added here as needed
import { Menu, MenuItem } from '../../models/menu';
import { User } from '../../models/user';
import { UserService } from '../../services/user-service';
import { Inbox } from '../../models/inbox';

@Component( {
  selector: 'main-header',
  imports: [
    RouterLink,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatMenuModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatBadgeModule,
    TranslatePipe
  ],
  templateUrl: './main-header.html',
  styleUrl: './main-header.css'
} )
export class MainHeader implements OnDestroy
{
  private inboxIntervalId: number = 0;
  protected readonly availableLangs = [{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }];
  protected defaultLang = 'en';
  protected title = 'Community Health Records';
  protected user: User | null = null; // Assuming User is a model that represents the user
  protected inbox: Inbox | null = null; // Assuming Inbox is a model that represents the user's inbox
  protected menu: Menu | null = null;
  protected appLogo: string = ''; // Assuming app_logo is a property in UserService
  protected numUnreadMessages: number = 0; // To track unread messages count

  constructor(
    @Inject( OKTA_CONFIG ) private oktaConfig: { oktaAuth: OktaAuth },
    private _userService: UserService,
    private _translateService: TranslateService
  ) { }

  ngOnInit(): void
  {
    // Initialize any necessary properties or services here
    this.getUser();
    this.getUserMenu();
    this.getUserInbox();

    // Start periodic inbox updates
    this.inboxIntervalId = setInterval( () =>
    {
      this.getUserInbox( true );
    }, 60000 );

    this.appLogo = this._userService.app_logo;
  }

  public async signOut(): Promise<void>
  {
    await this._userService.signOut();
  }

  public async getUser(): Promise<void>
  {
    this.user = this._userService.getUser();
  }

  public async getUserMenu(): Promise<void>
  {
    this.menu = await this._userService.getMenu();
    if ( !this.menu )
    {
      throw new Error( 'Menu is not defined' );
    }

    this.title = this.menu.appTitleLabel || 'Community Health Records';
  }

  public async getUserInbox( force: boolean = false ): Promise<void>
  {
    this.inbox = await this._userService.getInbox( force );

    if ( !this.inbox )
    {
      throw new Error( 'Inbox is not defined' );
    }
    this.numUnreadMessages = this.inbox.unreadMessages.length;
  }

  public async translateApp( event:any ): Promise<void>
  {
    this.defaultLang = event.value as string;
    this._translateService.use( event.value );
  }

  getMenuItemLabel( menu: MenuItem ): string
  {
    const labelObj = menu.label!;
    const labelText = labelObj[this.defaultLang as keyof typeof labelObj] || labelObj['en'] || '';
    return labelText;
  }

  ngOnDestroy(): void
  {
    if ( this.inboxIntervalId )
    {
      clearInterval( this.inboxIntervalId );
    }
  }

}
